/**
 * "Looking for a bus? Connect with Skip." — the buyer-side request.
 *
 * A buyer who cannot find what they need in the listings tells us what they
 * are looking for, and a person follows up. This is the validation contract
 * for that form: one definition, used by the React form before submit and by
 * the endpoint before anything is stored or sent, so the two can never
 * disagree about what a valid inquiry is.
 *
 * ## It is a request, not a listing and not an application
 *
 * Nothing here creates a listing, reserves a vehicle, quotes a price, or
 * starts a credit check. `financingAssistanceRequested` is a yes/no flag that
 * routes the conversation — it is not a finance application, and
 * `src/util/commercialInquiry.js` owns that distinction for the inquiry that
 * follows.
 *
 * ## Consent is a gate, not a field
 *
 * `consentToContact` must be explicitly true. There is no default, no
 * pre-ticked box, and no "by submitting you agree" — the buyer either said yes
 * or the inquiry is refused. This marketplace already works this way in
 * FinanceLeadForm, which disables submit until consent is given, and the
 * privacy gates in CI cover that behaviour.
 *
 * ## What callers must not do with the result
 *
 * `inquiry.email` and `inquiry.phone` are personal information. The existing
 * rebate-signup endpoint logs the lead's email address on every submission;
 * do not copy that. `redactForLog` exists so there is an obvious right way.
 */

// Explicit .js extensions throughout this chain. `yarn start` runs
// `node server/index.js` directly rather than the webpack bundle, and Node's
// ESM resolver does not guess extensions — server/api/dealer-apply.js imports
// this module, so an extensionless specifier here stops the server booting
// while the build still passes. Same reason sitemap.js requires
// '../../src/util/blogPages.js' with its extension.
import { BUS_TYPES } from './commercialPassenger.js';
import { OPERATOR_TYPE, isOrganisationalBuyer } from './commercialInquiry.js';

/** New or used. "Either" is a real answer, and the commonest one. */
export const CONDITION_PREFERENCE = {
  NEW: 'new',
  USED: 'used',
  EITHER: 'either',
};

/**
 * Budget bands rather than a free-text number.
 *
 * A band is enough to route the inquiry and it does not ask a school division
 * to publish its procurement ceiling to win a negotiation it has not started.
 * Values are inclusive lower bounds in whole dollars; `null` upper bound means
 * open-ended.
 */
export const BUDGET_BANDS = [
  { id: 'under-50k', label: 'Under $50,000', minCad: 0, maxCad: 50000 },
  { id: '50k-100k', label: '$50,000 – $100,000', minCad: 50000, maxCad: 100000 },
  { id: '100k-250k', label: '$100,000 – $250,000', minCad: 100000, maxCad: 250000 },
  { id: '250k-500k', label: '$250,000 – $500,000', minCad: 250000, maxCad: 500000 },
  { id: 'over-500k', label: 'Over $500,000', minCad: 500000, maxCad: null },
  { id: 'not-sure', label: 'Not sure yet', minCad: null, maxCad: null },
];

const BUDGET_IDS = BUDGET_BANDS.map(b => b.id);

const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Validate a "find me a bus" inquiry.
 *
 * Collects every error rather than stopping at the first, because a form that
 * reveals one problem per submit is a form people abandon.
 *
 * @returns {{valid: Boolean, errors: Object, inquiry: Object|null}}
 *   `errors` is keyed by field name so the form can place each message.
 */
export const validateBusRequestInquiry = ({
  name = '',
  organization = '',
  operatorType = null,
  email = '',
  phone = '',
  busType = null,
  conditionPreference = CONDITION_PREFERENCE.EITHER,
  passengerCapacity = null,
  budgetBand = null,
  province = null,
  community = '',
  deliveryNotes = '',
  financingAssistanceRequested = false,
  consentToContact = false,
} = {}) => {
  const errors = {};

  if (!String(name).trim()) {
    errors.name = 'Enter your name.';
  }

  if (!Object.values(OPERATOR_TYPE).includes(operatorType)) {
    errors.operatorType = 'Tell us who is buying.';
  } else if (isOrganisationalBuyer(operatorType) && !String(organization).trim()) {
    // A band, division or operator buys as the organisation. An individual
    // private buyer is not asked for one.
    errors.organization = 'Enter the name of your organization.';
  }

  if (!EMAIL_RE.test(String(email).trim())) {
    errors.email = 'Enter a valid email address.';
  }
  if (String(phone).replace(/\D/g, '').length < 10) {
    errors.phone = 'Enter a phone number with at least 10 digits.';
  }

  if (!Object.values(BUS_TYPES).includes(busType)) {
    errors.busType = 'Choose the type of bus you need.';
  }
  if (!Object.values(CONDITION_PREFERENCE).includes(conditionPreference)) {
    errors.conditionPreference = 'Choose new, used or either.';
  }

  // Capacity is optional — a charter operator may genuinely not know yet — but
  // a value that is present must be a sensible whole number of seats.
  if (passengerCapacity !== null && passengerCapacity !== '') {
    if (!Number.isInteger(passengerCapacity) || passengerCapacity < 1 || passengerCapacity > 200) {
      errors.passengerCapacity = 'Enter a passenger capacity between 1 and 200.';
    }
  }

  if (budgetBand !== null && !BUDGET_IDS.includes(budgetBand)) {
    errors.budgetBand = 'Choose a budget range.';
  }

  // Province or community: one or the other. A fly-in community may be the
  // only address a buyer can give, and refusing it would exclude exactly the
  // buyers this category is meant to reach.
  const hasProvince = PROVINCES.includes(province);
  const hasCommunity = !!String(community).trim();
  if (!hasProvince && !hasCommunity) {
    errors.province = 'Tell us your province or your community.';
  }

  if (typeof financingAssistanceRequested !== 'boolean') {
    errors.financingAssistanceRequested = 'Choose yes or no.';
  }

  // The gate. Explicit true, never a default.
  if (consentToContact !== true) {
    errors.consentToContact = 'We need your permission to contact you about this request.';
  }

  if (Object.keys(errors).length) {
    return { valid: false, errors, inquiry: null };
  }

  return {
    valid: true,
    errors: {},
    inquiry: {
      kind: 'bus-request-inquiry',
      name: String(name).trim(),
      organization: isOrganisationalBuyer(operatorType) ? String(organization).trim() : null,
      operatorType,
      email: String(email)
        .trim()
        .toLowerCase(),
      phone: String(phone).trim(),
      busType,
      conditionPreference,
      passengerCapacity:
        passengerCapacity === null || passengerCapacity === '' ? null : passengerCapacity,
      budgetBand: budgetBand || null,
      province: hasProvince ? province : null,
      community: hasCommunity ? String(community).trim() : null,
      deliveryNotes: String(deliveryNotes).trim() || null,
      financingAssistanceRequested,
      consentToContact: true,
      // Said plainly so nothing downstream reads this as more than it is.
      isListing: false,
      isApproval: false,
      reservesVehicle: false,
    },
  };
};

/**
 * The inquiry with personal information removed, safe to log.
 *
 * The existing rebate-signup endpoint writes the lead's email address to the
 * console on every submission. That is the pattern this function exists to
 * stop being repeated — call it, rather than deciding field by field at each
 * call site which parts are safe.
 *
 * Community is dropped too. In a small community the name plus a bus request
 * identifies a person as effectively as their phone number does.
 */
export const redactForLog = inquiry => {
  if (!inquiry) return null;
  return {
    kind: inquiry.kind,
    operatorType: inquiry.operatorType,
    busType: inquiry.busType,
    conditionPreference: inquiry.conditionPreference,
    passengerCapacity: inquiry.passengerCapacity,
    budgetBand: inquiry.budgetBand,
    province: inquiry.province,
    financingAssistanceRequested: inquiry.financingAssistanceRequested,
    hasDeliveryNotes: !!inquiry.deliveryNotes,
  };
};

/**
 * The inquiry shaped for the existing HubSpot contact upsert.
 *
 * Mirrors the property names server/api/rebate-signup.js already uses, so the
 * same HubSpot portal and the same `HUBSPOT_ACCESS_TOKEN` serve both and no
 * second CRM integration appears.
 *
 * @returns {Object} `{ properties: [{ property, value }] }`
 */
export const toHubSpotContactProperties = inquiry => {
  const [firstName, ...rest] = String(inquiry.name).split(' ');
  const capacity = inquiry.passengerCapacity
    ? `${inquiry.passengerCapacity} passengers`
    : 'not specified';
  const budget = BUDGET_BANDS.find(b => b.id === inquiry.budgetBand);

  const summary = [
    `Bus request — ${inquiry.busType}`,
    `Condition: ${inquiry.conditionPreference}`,
    `Capacity: ${capacity}`,
    `Budget: ${budget ? budget.label : 'not specified'}`,
    `Buyer: ${inquiry.operatorType}${inquiry.organization ? ` (${inquiry.organization})` : ''}`,
    `Location: ${[inquiry.community, inquiry.province].filter(Boolean).join(', ') ||
      'not specified'}`,
    `Financing assistance: ${inquiry.financingAssistanceRequested ? 'requested' : 'not requested'}`,
    inquiry.deliveryNotes ? `Delivery: ${inquiry.deliveryNotes}` : null,
  ]
    .filter(Boolean)
    .join(' | ');

  return {
    properties: [
      { property: 'firstname', value: firstName || '' },
      { property: 'lastname', value: rest.join(' ') },
      { property: 'email', value: inquiry.email },
      { property: 'phone', value: inquiry.phone },
      { property: 'state', value: inquiry.province || '' },
      { property: 'company', value: inquiry.organization || '' },
      { property: 'hs_lead_status', value: 'NEW' },
      { property: 'message', value: summary },
    ],
  };
};
